import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import config from "../../config";
import styles from "../css/css_pages/ClientManage.module.css";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { FaPhone, FaInfoCircle } from "react-icons/fa";
import {
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiEdit2,
  FiX,
  FiTrash2,
} from "react-icons/fi";

const ClientManagement = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [tsos, setTsos] = useState([]);
  const [selectedTsoFilter, setSelectedTsoFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modals
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);

    // Format as UTC time
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC", // Use UTC instead of local time
    };

    if (!showOnlyTime) {
      options.year = "numeric";
      options.month = "2-digit";
      options.day = "2-digit";
    }

    return date.toLocaleString("en-US", options).replace(",", "");
  };

  // Client data
  const [currentClient, setCurrentClient] = useState(null);

  // Simplified form data - only updatable fields
  const [formData, setFormData] = useState({
    type: "",
    tso_id: "",
    customer_name: "",
    address: "",
    phone: "",
    manager: "",
    chairman: "",
    mobile: "",
    email: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [tsoList, setTsoList] = useState([]);

  // Client types for dropdown
  const clientTypes = [
    { value: "SANASA Union", label: "SANASA Union" },
    { value: "Apex Federation", label: "Apex Federation" },
    { value: "COOP City", label: "COOP City" },
    { value: "COOP Society", label: "COOP Society" },
    { value: "CRB", label: "CRB" },
    { value: "CRB Union", label: "CRB Union" },
    { value: "Company", label: "Company" },
    { value: "NGO", label: "NGO" },
    { value: "PRDCS", label: "PRDCS" },
    { value: "SANASA Society", label: "SANASA Society" },
    { value: "Womens COOP", label: "Womens COOP" },
  ];

  // Fetch clients on component mount
  useEffect(() => {
    fetchData();
    fetchTsoList();
  }, []);

  // Update filtered clients when clients, search term, or TSO filter changes
  useEffect(() => {
    filterClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, searchTerm, selectedTsoFilter]);

  const filterClients = () => {
    let filtered = [...clients];

    // Filter by TSO
    if (selectedTsoFilter && selectedTsoFilter.value !== "all") {
      filtered = filtered.filter((client) => {
        // Compare client.tso_id with selected TSO value
        // Handle both string and number comparisons
        const clientTsoId = client.tso_id?.toString();
        const selectedTsoId = selectedTsoFilter.value?.toString();
        return clientTsoId === selectedTsoId;
      });
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (client) =>
          client.customer_name?.toLowerCase().includes(term) ||
          client.customer_id?.toString().includes(term) ||
          client.customer_no?.toString().includes(term) ||
          client.type?.toLowerCase().includes(term) ||
          client.tso_name?.toLowerCase().includes(term) ||
          client.manager?.toLowerCase().includes(term) ||
          client.address?.toLowerCase().includes(term) ||
          client.email?.toLowerCase().includes(term)
      );
    }

    setFilteredClients(filtered);
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefreshing(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      // Fetch clients and TSO list in parallel
      const [clientsResponse, tsoResponse] = await Promise.all([
        fetch(`${config.API_URL}/management/get-all-customers`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }),
        fetch(`${config.API_URL}/management/tso-list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!clientsResponse.ok) throw new Error("Failed to fetch clients");
      if (!tsoResponse.ok) throw new Error("Failed to fetch TSO list");

      const clientsData = await clientsResponse.json();
      const tsoData = await tsoResponse.json();

      console.log("Client data", clientsData);
      console.log("TSO data", tsoData);

      // Process clients data and ensure TSO names are properly mapped
      const processedClients = Array.isArray(clientsData.response)
        ? clientsData.response.map((client) => ({
            ...client,
            tso_name:
              client.tso_name ||
              tsoData.find(
                (tso) =>
                  tso.tso_emp_no === client.tso_id ||
                  tso.tso_id === client.tso_id
              )?.tso_name ||
              "Not assigned",
          }))
        : [];

      setClients(processedClients);
      setFilteredClients(processedClients);
      setTsoList(Array.isArray(tsoData) ? tsoData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchTsoList = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) return;

      const response = await fetch(`${config.API_URL}/management/tso-list`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTsos(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error fetching TSO list:", err);
    }
  };

  const fetchClientById = async (customer_id) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/get-customer/${customer_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch client details");

      const clientData = await response.json();
      console.log("Client Data from API", clientData);
      return clientData.response;
    } catch (error) {
      console.error("Error fetching client details:", error);
      setError(error.message);
      return null;
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = Cookies.get("authToken");
      const userId = Cookies.get("userId");

      if (!token || !userId) {
        setAlertMessage({
          text: "Authentication required. Please login again.",
          isError: true,
        });
        navigate("/login");
        return;
      }

      // Create the payload with only updatable fields
      const payload = {
        type: formData.type,
        tso_id: formData.tso_id,
        customer_name: formData.customer_name,
        address: formData.address,
        phone: formData.phone,
        manager: formData.manager,
        chairman: formData.chairman,
        mobile: formData.mobile,
        email: formData.email,
        user_id: userId,
      };

      console.log("Update Payload", payload);

      const response = await fetch(
        `${config.API_URL}/management/update-customer/${currentClient.customer_id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json();
      if (!response.ok || !responseData.success) {
        throw new Error(responseData.message || "Failed to update client");
      }

      // Fetch fresh client data from server to ensure consistency
      const updatedClientData = await fetchClientById(
        currentClient.customer_id
      );

      if (updatedClientData) {
        // Find and update TSO name
        const tsoInfo = tsoList.find(
          (tso) =>
            tso.tso_emp_no === updatedClientData.tso_id ||
            tso.tso_id === updatedClientData.tso_id
        );

        const clientWithTsoName = {
          ...updatedClientData,
          tso_name:
            tsoInfo?.tso_name || updatedClientData.tso_name || "Not assigned",
        };

        // Update the local state with fresh data
        setClients((prevClients) =>
          prevClients.map((client) =>
            client.customer_id === currentClient.customer_id
              ? clientWithTsoName
              : client
          )
        );
      }

      setAlertMessage({
        text: responseData.message || "Client updated successfully!",
        isError: false,
      });

      // Close the modal after a short delay to show the success message
      setTimeout(() => {
        setShowUpdateModal(false);
        setAlertMessage(null);
      }, 1500);
    } catch (error) {
      setAlertMessage({
        text: error.message || "An error occurred while updating client",
        isError: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClient = async () => {
    setIsSubmitting(true);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/delete-customer/${currentClient.customer_id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to delete client");
      }

      // Remove the deleted client from the local state
      setClients((prevClients) =>
        prevClients.filter(
          (client) => client.customer_id !== currentClient.customer_id
        )
      );

      // Close modal
      setShowDeleteConfirmModal(false);
    } catch (error) {
      console.error("Error deleting client:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkPhoneNumberExists = async (
    phone,
    excludeCurrentClient = false
  ) => {
    try {
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/management/check-phone/${phone}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            phone,
            exclude_customer_id: excludeCurrentClient
              ? currentClient?.customer_id
              : null,
          }),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || "Failed to check phone number");
      }

      return responseData.exists;
    } catch (error) {
      console.error("Error checking phone number:", error);
      return false;
    }
  };

  const validateForm = async () => {
    const newErrors = {};

    // Phone validation
    if (formData.phone) {
      const phoneExists = await checkPhoneNumberExists(formData.phone, true);
      if (phoneExists) {
        newErrors.phone = "Phone number already exists";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const navigateToCreateClient = () => {
    navigate("/create-client");
  };

  const openUpdateModal = async (client) => {
    const clientDetails = await fetchClientById(client.customer_id);
    if (clientDetails) {
      setCurrentClient(clientDetails);

      // Set only updatable fields in form data
      setFormData({
        type: clientDetails.type || "",
        tso_id: clientDetails.tso_id || "",
        customer_name: clientDetails.customer_name || "",
        address: clientDetails.address || "",
        phone: clientDetails.phone || "",
        manager: clientDetails.manager || "",
        chairman: clientDetails.chairman || "",
        mobile: clientDetails.mobile || "",
        email: clientDetails.email || "",
      });

      setErrors({});
      setAlertMessage(null);
      setShowUpdateModal(true);
    }
  };

  const openViewModal = async (client) => {
    const clientDetails = await fetchClientById(client.customer_id);
    if (clientDetails) {
      // Ensure TSO name is properly set
      const tsoInfo = tsoList.find(
        (tso) =>
          tso.tso_emp_no === clientDetails.tso_id ||
          tso.tso_id === clientDetails.tso_id
      );

      setCurrentClient({
        ...clientDetails,
        tso_name: tsoInfo?.tso_name || clientDetails.tso_name || "Not assigned",
      });
      setShowViewModal(true);
    }
  };

  const openDeleteConfirmModal = (client) => {
    setCurrentClient(client);
    setShowDeleteConfirmModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedTsoFilter(null);
  };

  // Loading state
  if (isLoading && !isRefreshing) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading Clients...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>Error: {error}</p>
        <button className={styles.retryButton} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  // Main render
  return (
    <div className={styles.invoiceManagement}>
      <header className={styles.managementHeader}>
        <div className={styles.headerTitleRow}>
          <h1>Client Management</h1>
          <button
            className={styles.refreshButton}
            disabled={isRefreshing}
            onClick={fetchData}
          >
            <FiRefreshCw className={isRefreshing ? styles.refreshing : ""} />
          </button>
        </div>

        <div className={styles.headerControls}>
          <div className={styles.controlsLeft}>
            <button
              className={styles.createBtn}
              onClick={navigateToCreateClient}
            >
              <FiPlus /> Create Client
            </button>
          </div>

          <div className={styles.controlsRight}>
            <div className={styles.tsoDropdown}>
              <Select
                options={[
                  { value: "all", label: "All TSOs" },
                  ...tsos.map((tso) => ({
                    value: tso.tso_emp_no || tso.tso_id,
                    label: tso.tso_calling_name || tso.tso_name,
                  })),
                ]}
                value={selectedTsoFilter}
                onChange={setSelectedTsoFilter}
                placeholder="Filter by TSO"
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "42px",
                    borderColor: "#e2e8f0",
                    "&:hover": { borderColor: "#cbd5e1" },
                  }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
            <div className={styles.searchBar}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search clients, TSO and Client Type..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      </header>

      <div className={styles.invoicesContainer}>
        <div className={styles.tableResponsiveWrapper}>
          {filteredClients.length > 0 ? (
            <>
              <div className={styles.tableInfo}>
                Showing {filteredClients.length} of {clients.length} clients
              </div>
              <div className={styles.tableScrollWrapper}>
                <table className={styles.invoicesTable}>
                  <thead>
                    <tr>
                      <th className={styles.numberColumn}>Client ID</th>
                      <th className={styles.clientColumn}>Client Name</th>
                      <th className={styles.typeColumn}>Client Type</th>
                      <th className={styles.amountColumn}>Assigned TSO</th>
                      <th className={styles.balanceColumn}>Manager Phone</th>
                      <th className={styles.actionsColumn}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => (
                      <tr
                        key={client.customer_id}
                        className={styles.clickableRow}
                        onClick={() => openViewModal(client)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className={styles.numberCell}>
                          {client.customer_no || client.customer_id}
                        </td>
                        <td className={styles.clientCell}>
                          {client.customer_name}
                        </td>
                        <td className={styles.typeCell}>
                          <span
                            className={`${styles.typeBadge} ${
                              styles[client.type?.replace(/\s+/g, "")] ||
                              styles.unknown
                            }`}
                          >
                            {client.type || "Not specified"}
                          </span>
                        </td>
                        <td className={styles.amountCell}>
                          {client.tso_name || "Not assigned"}
                        </td>
                        <td className={styles.balanceCell}>
                          {client.manager || "Not provided"}
                        </td>
                        <td className={styles.actionsCell}>
                          <button
                            className={styles.updateBtn}
                            title="Edit client"
                            onClick={(e) => {
                              e.stopPropagation();
                              openUpdateModal(client);
                            }}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className={styles.deleteBtn}
                            title="Delete client"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteConfirmModal(client);
                            }}
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={styles.noResults}>
              <p>No clients found matching your criteria.</p>
              {searchTerm && (
                <button className={styles.clearFilters} onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Simplified Update Client Modal */}
      {showUpdateModal && currentClient && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Update Client ({currentClient.customer_name})</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowUpdateModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              {alertMessage && (
                <div
                  className={
                    alertMessage.isError
                      ? styles.errorMessage
                      : styles.successMessage
                  }
                >
                  {alertMessage.text}
                </div>
              )}

              <form className={styles.formGroup} onSubmit={handleUpdateClient}>
                {/* Basic Information Section */}
                <div className="form-section">
                  <div className="section-header">
                    <h3>
                      <FaInfoCircle /> Basic Information
                    </h3>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Customer Type *</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className={`form-control ${errors.type ? "error" : ""}`}
                      required
                    >
                      <option value="">Select Customer Type</option>
                      {clientTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {errors.type && (
                      <span className="error-message">{errors.type}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Customer Name *</label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.customer_name ? "error" : ""
                      }`}
                      required
                    />
                    {errors.customer_name && (
                      <span className="error-message">
                        {errors.customer_name}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.address ? "error" : ""
                      }`}
                      rows="3"
                    />
                    {errors.address && (
                      <span className="error-message">{errors.address}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Assigned TSO</label>
                    <select
                      name="tso_id"
                      value={formData.tso_id}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="">Select a TSO</option>
                      {tsoList.map((tso) => (
                        <option
                          key={tso.tso_id || tso.tso_emp_no}
                          value={tso.tso_emp_no || tso.tso_id}
                        >
                          {tso.tso_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="form-section">
                  <div className="section-header">
                    <h3>
                      <FaPhone /> Contact Information
                    </h3>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-control ${errors.phone ? "error" : ""}`}
                    />
                    {errors.phone && (
                      <span className="error-message">{errors.phone}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Mobile</label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className={`form-control ${errors.mobile ? "error" : ""}`}
                    />
                    {errors.mobile && (
                      <span className="error-message">{errors.mobile}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-control ${errors.email ? "error" : ""}`}
                    />
                    {errors.email && (
                      <span className="error-message">{errors.email}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Manager</label>
                    <input
                      type="text"
                      name="manager"
                      value={formData.manager}
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.manager ? "error" : ""
                      }`}
                    />
                    {errors.manager && (
                      <span className="error-message">{errors.manager}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Chairman</label>
                    <input
                      type="text"
                      name="chairman"
                      value={formData.chairman}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={() => setShowUpdateModal(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Update Client"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Client Modal */}
      {showViewModal && currentClient && (
        <div className={styles.modalOverlay}>
          <div className={styles.largeModalContent}>
            <div className={styles.modalHeader}>
              <h2>Client Details ({currentClient.customer_name})</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowViewModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.invoiceSummary}>
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Client Name:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.customer_name || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Client Type:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.type || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Assigned TSO:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.tso_name || "Not assigned"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Manager:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.manager || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>
                      Customer Number:
                    </span>
                    <span className={styles.summaryValue}>
                      {currentClient.customer_no ||
                        currentClient.customer_id ||
                        "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>District:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.district || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Province:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.province || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>DS Division:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.ds_division || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Phone:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.phone || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Mobile:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.mobile || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Email:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.email || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Start Date:</span>
                    <span className={styles.summaryValue}>
                      {formatDateTime(currentClient.start_date)}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Agreement Date:</span>
                    <span className={styles.summaryValue}>
                      {formatDateTime(currentClient.agreement_date)}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>
                      Registration Number:
                    </span>
                    <span className={styles.summaryValue}>
                      {currentClient.reg_number || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>
                      Registration Date:
                    </span>
                    <span className={styles.summaryValue}>
                      {formatDateTime(currentClient.reg_date) || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Latitude:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.latitude || "Not provided"}
                    </span>
                  </div>

                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Longitude:</span>
                    <span className={styles.summaryValue}>
                      {currentClient.longitude || "Not provided"}
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.detailedInfo}>
                <div className={styles.infoSection}>
                  <h3 className={styles.sectionTitle}>Address</h3>
                  <p className={styles.sectionContent}>
                    {currentClient.address || "No address provided"}
                  </p>
                </div>

                <div className={styles.infoSection}>
                  <h3 className={styles.sectionTitle}>Notes</h3>
                  <p className={styles.sectionContent}>
                    {currentClient.notes || "No notes available"}
                  </p>
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.editButton}
                  onClick={() => {
                    setShowViewModal(false);
                    openUpdateModal(currentClient);
                  }}
                >
                  <FiEdit2 /> Edit Client
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={() => {
                    setShowViewModal(false);
                    openDeleteConfirmModal(currentClient);
                  }}
                >
                  <FiTrash2 /> Delete Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && currentClient && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmationModal}>
            <div className={styles.modalHeader}>
              <h2>Confirm Deletion</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowDeleteConfirmModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.confirmationContent}>
                <p className={styles.confirmationText}>
                  Are you sure you want to delete client:{" "}
                  <strong>{currentClient.customer_name}</strong> (ID:{" "}
                  {currentClient.customer_id})?
                </p>
                <p className={styles.confirmationWarning}>
                  This action cannot be undone. All associated data will be
                  permanently removed.
                </p>
              </div>

              <div className={styles.confirmationActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => setShowDeleteConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  className={styles.dangerButton}
                  onClick={handleDeleteClient}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Deleting..." : "Delete Client"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;
